import { Filters } from "@/types/filters";

interface GetSearchTitleParams {
	cityDisplayName: string;
	filters: Partial<Filters>;
}

/**
 * Constructs a dynamic search title based on active filters
 * Used in both header and list components for consistency
 *
 * Format: {bedrooms?} {propertyType?} in {locality?, }{city}
 * Examples:
 * - "Properties in Bangalore"
 * - "2BHK Apartment / Flat in Bangalore"
 * - "3BHK Villa in Koramangala, Bangalore"
 */
export function getSearchTitle({ cityDisplayName, filters }: GetSearchTitleParams): string {
	const parts: string[] = [];

	// Get selected location (same logic as breadcrumbs)
	// If selectedLocationId is provided, try to find matching location
	// If not found (e.g., ID not enriched yet), fall back to first location
	const selectedLocation =
		filters.locations?.find(location => location.id === filters.selectedLocationId) ?? filters.locations?.[0];

	// Add bedroom count if present
	if (filters.bedrooms && filters.bedrooms.min) {
		if (filters.bedrooms.min === filters.bedrooms.max) {
			parts.push(`${filters.bedrooms.min}BHK`);
		} else {
			parts.push(`${filters.bedrooms.min}-${filters.bedrooms.max}BHK`);
		}
	}

	// Add property type if present (singular form)
	if (filters.propertyType) {
		const propertyTypeMap: Record<string, string> = {
			apartment: "Flats / Apartments",
			house: "Houses",
			villa: "Villas",
			plot: "Plots",
		};
		const propertyTypeDisplay = propertyTypeMap[filters.propertyType] || filters.propertyType;
		parts.push(propertyTypeDisplay);
	}

	// If no specific filters, add "Properties"
	if (parts.length === 0) {
		parts.push("Properties");
	}
	parts.push("for sale");

	// Add location (locality or city)
	if (selectedLocation) {
		// Check if this is a project-based search (location type is "project")
		const isProjectSearch = selectedLocation.type === "project";
		const preposition = isProjectSearch ? "in and around" : "in";

		parts.push(`${preposition} ${selectedLocation.name}, ${cityDisplayName}`);
	} else {
		parts.push(`in ${cityDisplayName}`);
	}

	return parts.join(" ");
}
